import { get } from 'svelte/store'
import { apiConfig, cipherText, inappToken, inappTokenCipherText, inappTokenSource, memberInfo } from './inapp-store'
import { getAuthVAsync } from './openpoint'

// 提前 10 分鐘換發，避免使用者操作中途 token 剛好過期
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000

// 同一時間只允許一次換發請求；重複呼叫共用同一個 Promise
let refreshPromise = null
let refreshTimer = null

/**
 * 解碼 JWT payload（base64url → JSON）。
 * 解碼失敗時回傳 null，不拋錯。
 */
function decodeJwtPayload(token) {
    if (!token) { return null }

    try {
        const payload = token.split('.')[1]
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
        const padded = normalized.padEnd(normalized.length + (4 - normalized.length % 4) % 4, '=')
        return JSON.parse(window.atob(padded))
    } catch (error) {
        console.log('decodeJwtPayload error', error)
        return null
    }
}

/**
 * 取得 token 的到期時間（Unix ms）。
 * 無法解析時回傳 0。
 */
function getTokenExpiryMs(token) {
    const payload = decodeJwtPayload(token)
    return payload?.exp ? payload.exp * 1000 : 0
}

/**
 * 清除已排程的自動換發 timer。
 */
function clearRefreshTimer() {
    if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer)
        refreshTimer = null
    }
}

/**
 * token 換發失敗（401）時的統一處理。
 * 目前僅 console.warn；如需導向錯誤頁可在此擴充。
 */
function redirectToAuthError() {
    console.warn('[InappAuth] Authorization failed, token request returned 401.')
}

/**
 * 清除 token 及相關 store，並取消排程。
 */
function clearInappToken() {
    clearRefreshTimer()
    inappToken.set('')
    inappTokenCipherText.set('')
}

/**
 * 根據 token 到期時間排程自動換發（提前 TOKEN_REFRESH_BUFFER_MS）。
 */
function scheduleTokenRefresh(token) {
    clearRefreshTimer()

    const expiresAt = getTokenExpiryMs(token)
    if (!expiresAt) { return }

    const delay = expiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS
    if (delay <= 0) { return }

    refreshTimer = window.setTimeout(() => {
        refreshInappToken().catch((error) => {
            console.log('refreshInappToken timer error', error)
        })
    }, delay)
}

/**
 * 寫入新 token 並啟動自動換發排程。
 * @param {string} token
 * @param {{ cipherTextValue?: string }} options - 同步記錄簽發時使用的 ct，供換帳號偵測
 */
export function setInappToken(token, { cipherTextValue = '' } = {}) {
    inappToken.set(token)
    inappTokenCipherText.set(cipherTextValue)
    scheduleTokenRefresh(token)
}

/**
 * 判斷是否需要重新取得 token。
 * ct 路徑：ct 異動（換帳號）或 token 即將到期時換發。
 * cloudAuth 路徑：只看到期時間，不比對 ct（ct 對認證雲路徑無意義）。
 */
function shouldRefreshToken(token) {
    if (!token) { return true }

    if (get(inappTokenSource) === 'ct') {
        const currentCipherText = get(cipherText) || ''
        const tokenCipherText = get(inappTokenCipherText) || ''
        if (currentCipherText && currentCipherText !== tokenCipherText) { return true }
    }

    const expiresAt = getTokenExpiryMs(token)
    return !expiresAt || expiresAt - Date.now() < TOKEN_REFRESH_BUFFER_MS
}

/**
 * 判斷目前 token 是否仍有效（僅看到期時間，不比對 ct）。
 * 供 Home.svelte 在返回首頁時決定是否跳過 cloudAuth 流程。
 */
export function hasValidToken() {
    const token = get(inappToken)
    if (!token) { return false }
    const expiresAt = getTokenExpiryMs(token)
    return !!(expiresAt && expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS)
}

/**
 * 向後端換發 JWT。
 * 依 inappTokenSource 決定走 cloudAuth 路徑或 ct 路徑。
 */
async function requestToken() {
    if (get(inappTokenSource) === 'cloudAuth') {
        return requestCloudAuthToken()
    }
    return requestCtToken()
}

/**
 * ct 路徑換發 JWT。
 */
async function requestCtToken() {
    const currentCipherText = get(cipherText)
    const { apiUrl, apiKey } = get(apiConfig)

    if (!apiUrl || !apiKey || !currentCipherText) {
        clearInappToken()
        return ''
    }

    const response = await fetch(`${apiUrl}/inapp/token?code=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ct: currentCipherText })
    })

    if (response.status === 401) {
        clearInappToken()
        redirectToAuthError()
        throw new Error('INAPP_TOKEN_UNAUTHORIZED')
    }

    if (!response.ok) {
        throw new Error(`INAPP_TOKEN_REQUEST_FAILED_${response.status}`)
    }

    const data = await response.json()
    const token = data?.info?.token || ''

    if (!token) {
        throw new Error('INAPP_TOKEN_EMPTY')
    }

    setInappToken(token, { cipherTextValue: currentCipherText })

    return token
}

/**
 * 認證雲路徑換發 JWT：重取 authV → POST /inapp/cloud-auth。
 */
async function requestCloudAuthToken() {
    const { apiUrl, apiKey } = get(apiConfig)

    if (!apiUrl || !apiKey) {
        clearInappToken()
        return ''
    }

    const authV = await getAuthVAsync()
    const response = await fetch(`${apiUrl}/inapp/cloud-auth?code=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authV })
    })

    if (response.status === 401) {
        clearInappToken()
        redirectToAuthError()
        throw new Error('CLOUD_AUTH_UNAUTHORIZED')
    }

    if (!response.ok) {
        throw new Error(`CLOUD_AUTH_REQUEST_FAILED_${response.status}`)
    }

    const data = await response.json()
    const token = data?.info?.token || ''

    if (!token) {
        throw new Error('CLOUD_AUTH_TOKEN_EMPTY')
    }

    const info = get(memberInfo)
    memberInfo.set({ ...info, gid: data?.info?.gid || info.gid, foen: data?.info?.foen || '' })

    setInappToken(token, { cipherTextValue: '' })

    return token
}

/**
 * 觸發一次 token 換發。
 * 若已有換發進行中，回傳同一個 Promise（去重）。
 */
export async function refreshInappToken() {
    if (!refreshPromise) {
        refreshPromise = requestToken().finally(() => {
            refreshPromise = null
        })
    }

    return refreshPromise
}

/**
 * 確保持有有效 token，必要時自動換發。
 * @param {{ forceRefresh?: boolean }} options
 */
export async function ensureInappToken({ forceRefresh = false } = {}) {
    const token = get(inappToken)

    if (!forceRefresh && !shouldRefreshToken(token)) {
        return token
    }

    return refreshInappToken()
}

/**
 * 頁面初始化時呼叫：token 有效則排程換發；否則立即取得新 token。
 */
export function initializeInappAuth() {
    const token = get(inappToken)
    if (token && !shouldRefreshToken(token)) {
        scheduleTokenRefresh(token)
        return Promise.resolve(token)
    }

    return ensureInappToken().catch((error) => {
        console.log('initializeInappAuth error', error)
        return ''
    })
}

/**
 * 帶 JWT 的 fetch wrapper。
 * 遇到 401 自動換發一次並重試；若重試仍 401 則回傳原始回應。
 */
export async function inappFetch(input, init = {}) {
    const headers = new Headers(init.headers || {})
    const token = await ensureInappToken()
    if (token) {
        headers.set('Authorization', `Bearer ${token}`)
    }

    let response = await fetch(input, {
        ...init,
        headers
    })

    // 401 時強制換發一次再重試；若仍失敗則回傳原始 401 給呼叫端處理
    if (response.status !== 401) {
        return response
    }

    const refreshedToken = await ensureInappToken({ forceRefresh: true })
    if (!refreshedToken) {
        return response
    }

    headers.set('Authorization', `Bearer ${refreshedToken}`)
    response = await fetch(input, {
        ...init,
        headers
    })

    return response
}

const persistedToken = get(inappToken)
if (persistedToken) {
    scheduleTokenRefresh(persistedToken)
}
